import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Typography } from '../../../atoms/typography/Typography';
import { Row } from '../../../atoms/row/Row';
import { Stack } from '../../../atoms/stack/Stack';
import { LinkButton } from '../../../atoms/linkButton/LinkButton';

const DynamicHeightContainer = styled.div`
  display: flex;
  position: relative;
  flex: 1;
  background-color: ${(props) => props.$bgColor || 'white'};
  min-height: ${(props) => props.$minHeight || 'auto'};
  height: auto;

  @media (max-width: 767px) {
    height: 100%;
  }
`;

const SpanImage = styled.span`
  svg {
    width: 55px;
    height: 57px;
  }
`;

const SectionItem = React.memo(({ item }) => (
  <Row className="h-auto justify-between py-4 px-2 items-center max-w-md gap-4">
    <SpanImage>{item.icon}</SpanImage>
    <Stack className="w-fit h-full gap-0 justify-between lg:justify-center">
      <Typography fontVariant="md" textAlign="left" className="font-bold w-fit">
        {item.title}
      </Typography>
      <Typography
        fontVariant="md"
        textAlign="left"
        className="!text-customGray-600 w-fit"
      >
        {item.text}
      </Typography>
    </Stack>
  </Row>
));

const GuideSection = ({ section, customContent, shortTitle, bgColor }) => {
  const alignLeft = section.align === 'left';
  const buttonAvailable = section.actionButtonText && section.actionButtonHref;
  const [parentHeight, setParentHeight] = useState('auto');
  const imageRef = useRef(null);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      if (entries && entries[0]) {
        const { height } = entries[0].contentRect;
        setParentHeight(`${height + 150}px`);
      }
    });

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <DynamicHeightContainer $minHeight={parentHeight} $bgColor={bgColor}>
      <div
        className={`flex flex-1 flex-col min-[767px]:flex-row ${
          alignLeft ? 'justify-end' : 'justify-start'
        } py-5 md:py-10 lg:mx-auto max-w-6xl gap-5 max-md:gap-5`}
      >
        <Stack className="min-[767px]:flex-[0.5] justify-start md:justify-center mx-8">
          <Stack
            className={`md:max-w-md lg:max-w-lg lg:gap-5 pt-1 lg:pt-5 ${
              alignLeft ? 'lg:pl-20' : 'lg:pr-20'
            }`}
          >
            {shortTitle && (
              <Typography
                fontVariant="h8"
                textAlign="left"
                className="!text-secondary-400 w-fit"
              >
                {shortTitle}
              </Typography>
            )}
            <Typography
              fontVariant="h4"
              as="h2"
              textAlign="left"
              className="w-fit leading-tight"
            >
              {section.title}
            </Typography>
            {customContent || (
              <>
                {section.description.map((text) => (
                  <Typography
                    key={text}
                    fontVariant="lg"
                    textAlign="left"
                    className={`!text-customGray-600 w-fit ${
                      !buttonAvailable && 'lg:mb-10'
                    }`}
                  >
                    {text}
                  </Typography>
                ))}
                {buttonAvailable && (
                  <LinkButton
                    label={section.actionButtonText}
                    href={section.actionButtonHref}
                    className="w-fit mt-3"
                    size="button"
                    variant="primary-filled"
                  />
                )}
                {section.componentData.map((item) => (
                  <SectionItem key={item.title} item={item} />
                ))}
              </>
            )}
          </Stack>
        </Stack>
        <div
          className={`flex relative min-[767px]:absolute w-full min-[767px]:w-1/2 ${
            alignLeft ? 'justify-start pr-6 left-0' : 'justify-end pl-6 right-0'
          }`}
        >
          <img
            ref={imageRef}
            src={section.image}
            className={alignLeft ? 'rounded-r-[40px]' : 'rounded-l-[40px]'}
            alt="Systemguiden"
            width="658"
            height="840"
            loading="lazy"
          />
        </div>
      </div>
    </DynamicHeightContainer>
  );
};

export default GuideSection;
